import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../customAxios";

/**
 * @typedef {object} AssignmentData
 * @property {number} id
 * @property {string} warehouse_name
 * @property {string} company_name
 * @property {string} shipping_zone_name
 * @property {string} shipping_method
 */

/**
 * @typedef {object} CommuneData
 * @property {number} id
 * @property {string} name
 * @property {string} system_name
 * @property {string} postal_code
 * @property {string} settlement
 */

/**
 * @typedef {object} ShippingZoneData
 * @property {number} id
 * @property {string} name
 * @property {number | null} admin
 * @property {string} admin_email
 * @property {string} courier
 * @property {string} country
 * @property {string} price
 * @property {int} communes
 */

/**
 * Custom Hook to manage all state and logic for the Shipping Zones Detail page
 * Handles data fetching for the zone and its assignments, and CRUD for assignments
 *
 * @returns {{
 * zone: ShippingZoneData | null,
 * assignments: Array<AssignmentData>,
 * loading: boolean,
 * error: string | null,
 * createShippingZoneAssignment: (assignmentData: object) => Promise<void>,
 * updateShippingZoneAssignment: (assignmentId: number, assignmentData: object) => Promise<void>,
 * deleteShippingZoneAssignment: (assignmentId: number) => Promise<void>,
 * refetchZone: () => Promise<void>,
 * updateZone: (zoneId: number, zoneData: object) => Promise<void>,
 * refetchAssignments: () => Promise<void>
 * }}
 */
export function useShippingZoneDetails() {
  // Data status and IU
  const { zoneId } = useParams();
  const [zone, setZone] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const fetchData = useCallback(async () => {
    if (!zoneId) return;

    setLoading(true);
    setError(null);
    try {
      const [zoneResponse, assignmentsResponse] = await Promise.all([
        axios.get(`shipping_zones/${zoneId}/detail/`),
        axios.get(`warehouses/list_assignments_for_zone/${zoneId}/`),
      ]);
      setZone(zoneResponse.data);
      setAssignments(assignmentsResponse.data);
    } catch (error) {
      console.error(`Error al cargar la zona de despacho: ${error}`);
      setError("Error al cargar la zona de despacho");
    } finally {
      setLoading(false);
    }
  }, [zoneId]);

  const fetchZone = useCallback(async () => {
    if (!zoneId) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`shipping_zones/${zoneId}/detail/`);
      setZone(data);
    } catch (error) {
      console.error(`Error al cargar la zona de despacho: ${error}`);
      setError("Error al cargar la zona de despacho");
    } finally {
      setLoading(false);
    }
  }, [zoneId]);

  const updateZone = useCallback(
    async (zoneId, zoneData) => {
      setLoading(true);
      setError(null);
      try {
        if (zoneData.isMassiveUpload) {
          await massiveUpdate(zoneId, zoneData);
        } else {
          await axios.patch(`shipping_zones/${zoneId}/update/`, zoneData);
        }
        fetchZone();
      } catch (error) {
        console.error(`Error al actualizar zona de despacho: ${error}`);
        setError("Error al actualizar zona de despacho");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [zoneId, fetchZone],
  );

  const massiveUpdate = useCallback(async (zoneId, zoneData) => {
    const formData = new FormData();
    formData.append("name", zoneData.name);
    formData.append("courier", zoneData.courier);
    formData.append("price", zoneData.price);
    formData.append("admin_id", zoneData.admin);

    if (zoneData.tms) {
      formData.append("tms", zoneData.tms);
    }

    if (zoneData.file) {
      formData.append("file", zoneData.file);
    }

    return axios.patch(`shipping_zones/${zoneId}/update_massive/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }, []);

  const fetchAssignments = useCallback(async () => {
    if (!zoneId) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `warehouses/list_assignments_for_zone/${zoneId}/`,
      );
      setAssignments(data);
    } catch (error) {
      console.error(`Error al cargar la zona de despacho: ${error}`);
      setError("Error al cargar la zona de despacho");
    } finally {
      setLoading(false);
    }
  }, [zoneId]);

  const createShippingZoneAssignment = useCallback(
    async (assignmentData) => {
      const payload = { ...assignmentData, shipping_zone: parseInt(zoneId) };
      setLoading(true);
      setError(null);
      try {
        await axios.post("warehouses/create_warehouse_shipping_zone/", payload);
        await fetchAssignments();
      } catch (error) {
        console.error(`Error al crear asignación: ${error}`);
        setError("Error al crear asignación");
      } finally {
        setLoading(false);
      }
    },
    [zoneId, fetchAssignments],
  );

  const updateShippingZoneAssignment = useCallback(
    async (assignmentId, assignmentData) => {
      setLoading(true);
      setError(null);
      try {
        await axios.patch(
          `warehouses/update_warehouse_shipping_zone/${assignmentId}/`,
          assignmentData,
        );
        await fetchAssignments();
      } catch (error) {
        console.error(`Error al actualizar asignación: ${error}`);
        setError("Error al actualizar asignación");
      } finally {
        setLoading(false);
      }
    },
    [fetchAssignments],
  );

  const deleteShippingZoneAssignment = useCallback(
    async (assignmentId) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(
          `warehouses/delete_warehouse_shipping_zone/${assignmentId}/`,
        );
        await fetchAssignments();
      } catch (error) {
        console.error(`Error al eliminar asignación: ${error}`);
        setError("Error al eliminar asignación");
      } finally {
        setLoading(false);
      }
    },
    [fetchAssignments],
  );

  // SECONDARY EFFECTS (FETCH, DEBUNCE)
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    zone,
    assignments,
    loading,
    error,

    // MANNERS (Setters y Actions)
    refetchZone: fetchZone,
    updateZone,
    refetchAssignments: fetchAssignments,
    createShippingZoneAssignment,
    updateShippingZoneAssignment,
    deleteShippingZoneAssignment,
  };
}
